import { create } from 'zustand';
import type { ModelConfig, PromptVersion, RagConfig } from '@/types/config';
import {
  activatePromptVersion,
  createPromptVersion,
  type ConfigSide,
  getAppConfig,
  getPromptVersions,
  updateModelConfig,
  updateConfig,
  updateRagConfig,
} from '@/api/config';
import { DEFAULTS } from '@/utils/constants';

interface ConfigState {
  leftConfig: ModelConfig;
  rightConfig: ModelConfig;
  systemPrompt: string;
  systemPromptVersion: string;
  systemPrompts: Record<ConfigSide, string>;
  systemPromptVersionsBySide: Record<ConfigSide, string>;
  promptVersions: PromptVersion[];
  promptVersionsBySide: Record<ConfigSide, PromptVersion[]>;
  ragEnabled: boolean;
  ragTopK: number;
  ragConfigs: Record<ConfigSide, RagConfig>;
  loading: boolean;
  error: string | null;
}

interface ConfigActions {
  loadConfig: () => Promise<void>;
  updateModelConfig: (side: 'left' | 'right', cfg: Partial<ModelConfig>) => Promise<void>;
  updateSystemPrompt: (text: string) => Promise<void>;
  loadPromptVersions: (side?: ConfigSide) => Promise<void>;
  savePromptVersion: (text: string, name?: string, side?: ConfigSide) => Promise<void>;
  activatePromptVersion: (version: string, side?: ConfigSide) => Promise<void>;
  toggleRag: (enabled: boolean, side?: ConfigSide) => Promise<void>;
  updateRagTopK: (topK: number, side?: ConfigSide) => Promise<void>;
  clearError: () => void;
}

type ConfigStore = ConfigState & ConfigActions;

const defaultModelConfig: ModelConfig = {
  name: '默认模型',
  apiBaseUrl: '',
  apiKey: '',
  model: 'gpt-4o',
  temperature: DEFAULTS.TEMPERATURE,
  maxTokens: DEFAULTS.MAX_TOKENS,
};

export const useConfigStore = create<ConfigStore>((set, get) => ({
  leftConfig: { ...defaultModelConfig, name: '模型 A' },
  rightConfig: { ...defaultModelConfig, name: '模型 B' },
  systemPrompt: '',
  systemPromptVersion: 'coach_v1',
  systemPrompts: { left: '', right: '' },
  systemPromptVersionsBySide: { left: 'coach_v1', right: 'coach_v1' },
  promptVersions: [],
  promptVersionsBySide: { left: [], right: [] },
  ragEnabled: false,
  ragTopK: DEFAULTS.RAG_TOP_K,
  ragConfigs: {
    left: { enabled: false, top_k: DEFAULTS.RAG_TOP_K },
    right: { enabled: false, top_k: DEFAULTS.RAG_TOP_K },
  },
  loading: false,
  error: null,

  loadConfig: async () => {
    set({ loading: true, error: null });
    try {
      const appConfig = await getAppConfig();
      set({
        leftConfig: appConfig.leftModel || get().leftConfig,
        rightConfig: appConfig.rightModel || get().rightConfig,
        systemPrompt: appConfig.leftSystemPrompt || appConfig.systemPrompt || '',
        systemPromptVersion: appConfig.leftSystemPromptVersion || appConfig.systemPromptVersion || 'coach_v1',
        systemPrompts: {
          left: appConfig.leftSystemPrompt || appConfig.systemPrompt || '',
          right: appConfig.rightSystemPrompt || appConfig.systemPrompt || '',
        },
        systemPromptVersionsBySide: {
          left: appConfig.leftSystemPromptVersion || appConfig.systemPromptVersion || 'coach_v1',
          right: appConfig.rightSystemPromptVersion || appConfig.systemPromptVersion || 'coach_v1',
        },
        ragEnabled: appConfig.leftRag?.enabled ?? appConfig.ragEnabled ?? false,
        ragTopK: appConfig.leftRag?.top_k ?? appConfig.ragTopK ?? DEFAULTS.RAG_TOP_K,
        ragConfigs: {
          left: appConfig.leftRag || { enabled: appConfig.ragEnabled || false, top_k: appConfig.ragTopK || DEFAULTS.RAG_TOP_K },
          right: appConfig.rightRag || { enabled: appConfig.ragEnabled || false, top_k: appConfig.ragTopK || DEFAULTS.RAG_TOP_K },
        },
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '加载配置失败';
      set({ error: message, loading: false });
    }
  },

  updateModelConfig: async (side: 'left' | 'right', cfg: Partial<ModelConfig>) => {
    set({ loading: true, error: null });
    try {
      const current = side === 'left' ? get().leftConfig : get().rightConfig;
      const updated = { ...current, ...cfg };
      await updateModelConfig(side, updated);
      set({
        [side === 'left' ? 'leftConfig' : 'rightConfig']: updated,
        loading: false,
      } as unknown as Partial<ConfigState>);
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新模型配置失败';
      set({ error: message, loading: false });
    }
  },

  updateSystemPrompt: async (text: string) => {
    set({ loading: true, error: null });
    try {
      await updateConfig('system_prompt', text);
      set({ systemPrompt: text, loading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新 System Prompt 失败';
      set({ error: message, loading: false });
    }
  },

  loadPromptVersions: async (side: ConfigSide = 'left') => {
    set({ loading: true, error: null });
    try {
      const versions = await getPromptVersions(side);
      const active = versions.find((item) => item.active);
      set({
        promptVersions: side === 'left' ? versions : get().promptVersions,
        promptVersionsBySide: { ...get().promptVersionsBySide, [side]: versions },
        systemPromptVersion: side === 'left' ? active?.version || get().systemPromptVersion : get().systemPromptVersion,
        systemPrompt: side === 'left' ? active?.text || get().systemPrompt : get().systemPrompt,
        systemPromptVersionsBySide: {
          ...get().systemPromptVersionsBySide,
          [side]: active?.version || get().systemPromptVersionsBySide[side],
        },
        systemPrompts: {
          ...get().systemPrompts,
          [side]: active?.text || get().systemPrompts[side],
        },
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '加载 Prompt 版本失败';
      set({ error: message, loading: false });
    }
  },

  savePromptVersion: async (text: string, name?: string, side: ConfigSide = 'left') => {
    set({ loading: true, error: null });
    try {
      const version = await createPromptVersion(text, name, side);
      const versions = await getPromptVersions(side);
      set({
        promptVersions: side === 'left' ? versions : get().promptVersions,
        promptVersionsBySide: { ...get().promptVersionsBySide, [side]: versions },
        systemPrompt: side === 'left' ? version.text : get().systemPrompt,
        systemPromptVersion: side === 'left' ? version.version : get().systemPromptVersion,
        systemPrompts: { ...get().systemPrompts, [side]: version.text },
        systemPromptVersionsBySide: { ...get().systemPromptVersionsBySide, [side]: version.version },
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '保存 Prompt 新版本失败';
      set({ error: message, loading: false });
    }
  },

  activatePromptVersion: async (version: string, side: ConfigSide = 'left') => {
    set({ loading: true, error: null });
    try {
      const active = await activatePromptVersion(version, side);
      const versions = await getPromptVersions(side);
      set({
        promptVersions: side === 'left' ? versions : get().promptVersions,
        promptVersionsBySide: { ...get().promptVersionsBySide, [side]: versions },
        systemPrompt: side === 'left' ? active.text : get().systemPrompt,
        systemPromptVersion: side === 'left' ? active.version : get().systemPromptVersion,
        systemPrompts: { ...get().systemPrompts, [side]: active.text },
        systemPromptVersionsBySide: { ...get().systemPromptVersionsBySide, [side]: active.version },
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '切换 Prompt 版本失败';
      set({ error: message, loading: false });
    }
  },

  toggleRag: async (enabled: boolean, side: ConfigSide = 'left') => {
    set({ loading: true, error: null });
    try {
      const current = get().ragConfigs[side];
      const updated = await updateRagConfig(side, { ...current, enabled });
      set({
        ragEnabled: side === 'left' ? updated.enabled : get().ragEnabled,
        ragTopK: side === 'left' ? updated.top_k : get().ragTopK,
        ragConfigs: { ...get().ragConfigs, [side]: updated },
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新 RAG 配置失败';
      set({ error: message, loading: false });
    }
  },

  updateRagTopK: async (topK: number, side: ConfigSide = 'left') => {
    set({ loading: true, error: null });
    try {
      const current = get().ragConfigs[side];
      const updated = await updateRagConfig(side, { ...current, top_k: topK });
      set({
        ragEnabled: side === 'left' ? updated.enabled : get().ragEnabled,
        ragTopK: side === 'left' ? updated.top_k : get().ragTopK,
        ragConfigs: { ...get().ragConfigs, [side]: updated },
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新 RAG Top-K 失败';
      set({ error: message, loading: false });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
